import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { LazyAvatar } from './LazyAvatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  LayoutDashboard, 
  Users, 
  CheckCircle2, 
  Trophy, 
  Medal, 
  Bell, 
  User,
  LogOut,
  Settings,
  Menu,
  X,
  Shield,
  FileCheck,
  UserCheck,
  Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Alnoor Islamic Center Logo
const LOGO_URL = '/AIC-Logo.png';

export const Layout = ({ children }) => {
  const { user, logout, isAdmin, getAuthHeaders } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user) return;
      try {
        const response = await fetch(`${API_URL}/api/notifications/unread/count`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setUnreadCount(data.count);
        }
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user, getAuthHeaders]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/children', label: 'Children', icon: Users },
    { path: '/tasks', label: 'Tasks', icon: CheckCircle2 },
    { path: '/achievements', label: 'Achievements', icon: Trophy },
    { path: '/leaderboard', label: 'Leaderboard', icon: Medal },
  ];

  const adminNavItems = [
    { path: '/admin', label: 'Admin Dashboard', icon: Shield },
    { path: '/admin/tasks', label: 'Manage Tasks', icon: CheckCircle2 },
    { path: '/admin/achievements', label: 'Manage Achievements', icon: Trophy },
    { path: '/admin/children', label: 'Children', icon: UserCheck },
    { path: '/admin/users', label: 'Users', icon: Users },
    { path: '/admin/approvals', label: 'Approvals', icon: FileCheck },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#FFFBEB] islamic-pattern">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow h-full bg-white border-r border-[#D4AF37]/20 shadow-lg overflow-hidden">
          {/* Logo */}
          <Link to="/" className="relative p-2">
            <div className="flex items-center gap-3 px-6 py-5 border-b border-[#D4AF37]/20">
              <img 
                src={LOGO_URL} 
                alt="Alnoor Islamic Center" 
                className="h-10 w-10 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div>
                <h1 className="text-lg font-bold text-[#08513F] font-outfit leading-tight">AIC Super</h1>
                <p className="text-xs text-[#D4AF37] font-semibold">Muslims Challenge</p>
              </div>
            </div>
          </Link>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive(item.path)
                    ? 'bg-[#08513F] text-white shadow-lg'
                    : 'text-[#264653] hover:bg-[#08513F]/10'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}

            {isAdmin && (
              <>
                <div className="pt-4 pb-2">
                  <p className="px-4 text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">Admin</p>
                </div>
                {adminNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-admin-${item.label.toLowerCase().replace(' ', '-')}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                      isActive(item.path)
                        ? 'bg-[#08513F] text-white shadow-lg'
                        : 'text-[#264653] hover:bg-[#08513F]/10'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                ))}
              </>
            )}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-[#D4AF37]/20">
            <div className="flex items-center gap-3 px-2">
              <LazyAvatar
                src={user?.picture}
                fallback={<span className="bg-[#08513F] text-white">{user?.full_name?.charAt(0) || 'U'}</span>}
                className="h-10 w-10 border-2 border-[#D4AF37]"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#264653] truncate">{user?.full_name}</p>
                <p className="text-xs text-[#6B7280] truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#D4AF37]/20 shadow-md">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <img 
              src={LOGO_URL} 
              alt="Alnoor Islamic Center" 
              className="h-8 w-8 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <span className="text-lg font-bold text-[#08513F] font-outfit">AIC Challenge</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/notifications" className="relative p-2">
              <Bell className="w-5 h-5 text-[#264653]" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center bg-[#E76F51] text-white text-xs font-bold rounded-full">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-[#264653]" />
              ) : (
                <Menu className="w-6 h-6 text-[#264653]" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="fixed top-16 left-0 right-0 bottom-16 bg-white border-b border-[#D4AF37]/20 shadow-lg animate-slide-up overflow-y-auto z-40">
            <nav className="p-4 space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl ${
                    isActive(item.path)
                      ? 'bg-[#08513F] text-white'
                      : 'text-[#264653]'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              ))}

              {isAdmin && (
                <>
                  <div className="pt-4 pb-2">
                    <p className="px-4 text-xs font-semibold text-[#D4AF37] uppercase">Admin</p>
                  </div>
                  {adminNavItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl ${
                        isActive(item.path)
                          ? 'bg-[#08513F] text-white'
                          : 'text-[#264653]'
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  ))}
                </>
              )}

              <div className="pt-4 border-t border-[#D4AF37]/20">
                <Link
                  to="/profile"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-[#264653]"
                >
                  <User className="w-5 h-5" />
                  <span className="font-medium">Profile</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 text-red-500 w-full"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Logout</span>
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Top Bar (Desktop) */}
      <div className="hidden lg:block fixed top-0 left-64 right-0 z-40 bg-white/80 backdrop-blur-md border-b border-[#D4AF37]/20">
        <div className="flex items-center justify-end px-6 py-3">
          <div className="flex items-center gap-4">
            <Link
              to="/notifications"
              className="relative p-2 rounded-full hover:bg-[#08513F]/10 transition-colors"
              data-testid="notifications-link"
            >
              <Bell className="w-5 h-5 text-[#264653]" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center bg-[#E76F51] text-white text-xs font-bold rounded-full animate-pulse">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="user-menu-trigger">
                    <LazyAvatar
                      src={user?.picture}
                      fallback={<span className="bg-[#08513F] text-white text-sm">{user?.full_name?.charAt(0) || 'U'}</span>}
                      className="h-8 w-8 border-2 border-[#D4AF37]"
                    />
                  <span className="text-sm font-medium text-[#264653]">{user?.full_name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                    <User className="w-4 h-4" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-500 cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 pb-20 lg:pt-14 min-h-screen">
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#D4AF37]/20 shadow-lg z-50">
        <div className="flex items-center justify-around py-2">
          {navItems.slice(0, 4).map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl ${
                isActive(item.path)
                  ? 'text-[#08513F]'
                  : 'text-[#6B7280]'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs font-medium">{item.label.split(' ')[0]}</span>
            </Link>
          ))}
          <Link
            to="/leaderboard"
            className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl ${
              isActive('/leaderboard')
                ? 'text-[#08513F]'
                : 'text-[#6B7280]'
            }`}
          >
            <Medal className="w-5 h-5" />
            <span className="text-xs font-medium">Ranks</span>
          </Link>
        </div>
      </nav>
    </div>
  );
};

export default Layout;
